<template>
  <v-dialog v-model="dialog" max-width="600" persistent @input="onDialogChange">
    <template v-slot:activator="{ on, attrs }">
      <v-btn
        color="error"
        v-bind="attrs"
        v-on="on"
        v-if="canAnonymize"
        @click="onAnonymizeButtonClick"
      >
        <v-icon left small>mdi-account-remove</v-icon>
        Anonymize Customer
      </v-btn>
    </template>

    <v-card>
      <v-card-title class="headline">
        <v-icon color="red" class="mr-2">mdi-alert</v-icon>
        Customer Anonymization Request
      </v-card-title>

      <v-card-text>
        <v-alert type="warning" prominent icon="mdi-alert-octagon-outline" class="ma-4 pa-4">
          <strong>Warning:</strong> This action is PERMANENT and cannot be reversed.
          All personal data for customer ID <strong>{{ customerId }}</strong> will be anonymized.
        </v-alert>

        <v-form ref="form" v-model="formValid" @submit.prevent="submitRequest" class="mt-8">
          <v-select
            v-model="reason"
            :items="reasonOptions"
            :rules="[rules.required]"
            label="Reason for Anonymization"
            outlined
            required
          ></v-select>


          <v-checkbox
            v-model="confirmUnderstand"
            :rules="[rules.checkbox]"
            color="primary"
            dense
          >
            <template v-slot:label>
              <span>
                I understand this action is permanent and irreversible
              </span>
            </template>
          </v-checkbox>

          <v-checkbox
            v-model="confirmAuthorized"
            :rules="[rules.checkbox]"
            color="primary"
            dense
          >
            <template v-slot:label>
              <span>
                I am authorized to make this request
              </span>
            </template>
          </v-checkbox>
        </v-form>
      </v-card-text>

      <v-divider></v-divider>

      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn
          text
          @click="cancelRequest"
          :disabled="loading"
        >
          Cancel
        </v-btn>
        <v-btn
          color="error"
          @click="submitRequest"
          :loading="loading"
          :disabled="!canSubmit"
        >
          Confirm Anonymization
        </v-btn>
      </v-card-actions>
    </v-card>

    <!-- Result Dialog -->
    <v-dialog v-model="resultDialog" max-width="500">
      <v-card>
        <v-card-title>
          <v-icon :color="success ? 'success' : 'error'" class="mr-2">
            {{ success ? 'mdi-check-circle' : 'mdi-alert-circle' }}
          </v-icon>
          {{ success ? 'Request Submitted' : 'Request Failed' }}
        </v-card-title>
        
        <v-card-text>
          <p>{{ resultMessage }}</p>
          <div v-if="jobInfo" class="mt-3">
            <div><strong>Job ID:</strong> {{ jobInfo.job_id }}</div>
            <div><strong>Status:</strong> {{ jobInfo.status }}</div>
            <div><strong>Requested at:</strong> {{ formatDate(jobInfo.requested_at) }}</div>
          </div>
        </v-card-text>
        
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" text @click="closeResult">OK</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-dialog>
</template>

<script>
import { mapGetters } from 'vuex'
import customerAnonymizationApi from '@/services/api/customerAnonymizationApi'

export default {
  name: 'AnonymizationModal',
  
  props: {
    customerId: {
      type: [String, Number],
      required: true
    },
    customerName: {
      type: String,
      default: ''
    }
  },
  
  data() {
    return {
      dialog: false,
      loading: false,
      formValid: false,
      
      // Form fields
      reason: '',
      confirmUnderstand: false,
      confirmAuthorized: false,
      
      // Result dialog
      resultDialog: false,
      success: false,
      resultMessage: '',
      jobInfo: null,
      
      // Options
      reasonOptions: [
        { text: 'MCDPA Request', value: 'MCDPA_REQUEST' },
        { text: 'Data Retention Policy', value: 'DATA_RETENTION_POLICY' },
        { text: 'Legal Requirement', value: 'LEGAL_REQUIREMENT' },
        { text: 'Other', value: 'OTHER' }
      ],
      
      // Validation
      rules: {
        required: v => !!v || 'Required',
        checkbox: v => v === true || 'You must confirm this requirement'
      }
    }
  },
  
  mounted() {
    // Component mounted
  },
  
  computed: {
    ...mapGetters(['currentUser', 'isAdmin', 'isAccountAdmin']),
    
    canAnonymize() {
      return this.isAdmin || this.isAccountAdmin
    },
    
    canSubmit() {
      return this.formValid && 
             this.confirmUnderstand && 
             this.confirmAuthorized && 
             !this.loading
    }
  },
  
  methods: {
    onAnonymizeButtonClick() {
      // Button click handled by Vuetify dialog
    },
    
    onDialogChange(value) {
      // Dialog state changed
    },
    
    cancelRequest() {
      this.dialog = false
      this.resetForm()
    },
    
    resetForm() {
      if (this.$refs.form) {
        this.$refs.form.reset()
      }
      this.reason = ''
      this.confirmUnderstand = false
      this.confirmAuthorized = false
    },
    
    async submitRequest() {
      if (!this.$refs.form.validate()) return
      if (!this.canSubmit) return
      
      this.loading = true
      
      try {
        const payload = {
          anonymization: {
            reason: this.reason || 'MCDPA_REQUEST' // Use default if not selected
          }
        }
        
        const response = await customerAnonymizationApi.create(this.customerId, payload)
        
        this.success = true
        this.jobInfo = response
        this.resultMessage = `Anonymization request for customer ${this.customerId} has been submitted and will be processed asynchronously.`
        
        // Emit success event
        this.$emit('anonymization-requested', {
          customerId: this.customerId,
          jobInfo: this.jobInfo
        })
        
      } catch (error) {
        this.success = false
        this.resultMessage = error.message || 'Failed to submit anonymization request. Please try again.'
        this.jobInfo = null
      } finally {
        this.loading = false
        this.dialog = false
        this.resultDialog = true
      }
    },
    
    closeResult() {
      this.resultDialog = false
      this.resetForm()
      
      if (this.success) {
        // Optionally refresh the page or notify parent component
        this.$emit('anonymization-complete')
      }
    },
    
    formatDate(dateString) {
      if (!dateString) return ''
      return new Date(dateString).toLocaleString()
    }
  }
}
</script>

<style scoped>
.v-dialog--active {
  overflow-y: auto;
}
</style>